import React, { useState } from 'react';
import { usePackCopyContext } from '../contexts/PackCopyContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHashtag,
    faCopy,
    faTrash,
    faPen,
} from '@fortawesome/free-solid-svg-icons';
import { useProjectDetailsContext } from '../contexts/ProjectDetailsContext';
import { faCalendar, faUser } from '@fortawesome/free-regular-svg-icons';
import { formatDate } from '../utils/timeUtils';
import styled from 'styled-components';
import { MacScrollbar } from 'mac-scrollbar';
import EllipsisOptions from './EllipsisOptions';
import TextInputModal from './modals/TextInputModal';
import PackCopySpecs from './PackCopySpecs';

const Scrollbar = styled(MacScrollbar)`
    flex: 1; /* Take up remaining space */
    overflow-y: auto; /* Enable scrolling */
    width: 100%;
    // display: flex;
    // flex-direction: column;
`;

const PackCopyDetails: React.FC = () => {
    const {
        state: { userDetailsMapping },
    } = useProjectDetailsContext();
    const {
        state: { packCopyDetailed },
        handlers: {
            handlePackCopyNameUpdate,
            handlePackCopyNotesUpdate,
            handleSpecCreation,
        },
    } = usePackCopyContext();
    const userDetails = userDetailsMapping[packCopyDetailed.creation.user_id];
    const userName = userDetails
        ? `${userDetails.first_name} ${userDetails.last_name}`
        : 'Unknown User';

    // State for modals
    const [isEditNameModalOpen, setIsEditNameModalOpen] = useState(false);
    const [isEditNotesModalOpen, setIsEditNotesModalOpen] = useState(false);

    const {
        handlers: { handlePackCopyDuplicate, handlePackCopyDelete },
    } = useProjectDetailsContext();

    // Handler for updating pack copy name
    const handleNameUpdate = (newName: string) => {
        handlePackCopyNameUpdate(packCopyDetailed.id, newName);
        setIsEditNameModalOpen(false);
    };

    // Handler for updating pack copy notes
    const handleNotesUpdate = (newNotes: string) => {
        handlePackCopyNotesUpdate(packCopyDetailed.id, newNotes);
        setIsEditNotesModalOpen(false);
    };

    return (
        <Scrollbar
            trackStyle={(horizontal) => ({
                [horizontal ? 'height' : 'width']: 0,
                right: 0,
                border: 0,
            })}
            thumbStyle={(horizontal) => ({
                [horizontal ? 'height' : 'width']: 6,
            })}
        >
            <div className="centred-max-width-1100 pack-copy-details">
                <div className="header-container">
                    <h2>{packCopyDetailed.name}</h2>
                    <EllipsisOptions
                        options={[
                            {
                                icon: faPen,
                                label: 'Edit name',
                                onClick: () => {
                                    setIsEditNameModalOpen(true);
                                },
                            },
                            {
                                icon: faPen,
                                label: 'Edit notes',
                                onClick: () => {
                                    setIsEditNotesModalOpen(true);
                                },
                            },
                            {
                                icon: faCopy,
                                label: 'Duplicate',
                                onClick: () => {
                                    handlePackCopyDuplicate(
                                        packCopyDetailed.id,
                                    );
                                },
                            },
                            {
                                icon: faTrash,
                                label: 'Delete',
                                onClick: () => {
                                    handlePackCopyDelete(packCopyDetailed.id);
                                },
                                variant: 'danger',
                            },
                        ]}
                    />
                </div>
                {packCopyDetailed.notes && (
                    <p className="notes">{packCopyDetailed.notes}</p>
                )}
                <div className="metadata-container">
                    <div className="metadata-item">
                        <FontAwesomeIcon icon={faCalendar} className="icon" />
                        <p className="value">
                            {formatDate(
                                packCopyDetailed.creation.timestamp,
                                true,
                                true,
                            )}
                        </p>
                    </div>
                    <div className="metadata-item">
                        <FontAwesomeIcon icon={faUser} className="icon" />
                        <p className="value">{userName}</p>
                    </div>
                    <div className="metadata-item">
                        <FontAwesomeIcon icon={faHashtag} className="icon" />
                        <p className="value">{packCopyDetailed.id}</p>
                    </div>
                </div>
                <hr />
                <PackCopySpecs
                    specs={packCopyDetailed.specs}
                    onSpecCreation={handleSpecCreation}
                    useContext={true}
                />
            </div>
            {isEditNameModalOpen && (
                <TextInputModal
                    isOpen={isEditNameModalOpen}
                    onClose={() => setIsEditNameModalOpen(false)}
                    heading="Edit Pack Copy Name"
                    defaultValue={packCopyDetailed.name}
                    onConfirm={handleNameUpdate}
                    confirmButtonText="Save Name"
                />
            )}
            {isEditNotesModalOpen && (
                <TextInputModal
                    isOpen={isEditNotesModalOpen}
                    onClose={() => setIsEditNotesModalOpen(false)}
                    heading="Edit Pack Copy Notes"
                    defaultValue={packCopyDetailed.notes || ''}
                    onConfirm={handleNotesUpdate}
                    confirmButtonText="Save Notes"
                />
            )}
        </Scrollbar>
    );
};

export default PackCopyDetails;
